import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeftRight, ShoppingCart, SlidersHorizontal } from 'lucide-react'
import { getInventoryItems, getSkuBins, getStockLevels, getStockMovements, listCostLayers } from '../../api/inventory'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { BackLink, DocHeader, StatusBadge } from '../../components/PageParts'
import { fmtDay } from '../../utils/format'
import { useSession } from '../../session'
import { money, num } from '../sales/salesShared'
import { MOVE_TYPES, qty, stockState, stockValue, unitCost } from './inventoryShared'

// One stock item: how many, where, what they cost, and what moved lately.
export default function ProductStock() {
  const { productId } = useParams()
  const { roles, binTracking } = useSession()
  const canBuy = roles.includes('admin') || roles.includes('purchase')
  const [item, setItem] = useState(null)
  const [levels, setLevels] = useState([])
  const [layers, setLayers] = useState([])
  const [bins, setBins] = useState(null)
  const [moves, setMoves] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    ;(async () => {
      try {
        const all = await getInventoryItems()
        const found = (all || []).find((p) => String(p.product_id) === String(productId))
        if (cancelled) return
        if (!found) { setError('This product could not be found.'); return }
        setItem(found)
        const [lv, ly, mv, bn] = await Promise.all([
          getStockLevels(productId).catch(() => []),
          listCostLayers({ product_id: productId }).catch(() => []),
          found.sku ? getStockMovements({ sku: found.sku, limit: 10 }).catch(() => []) : [],
          binTracking && found.sku ? getSkuBins(found.sku).catch(() => null) : null,
        ])
        if (cancelled) return
        setLevels(Array.isArray(lv) ? lv : [])
        setLayers((Array.isArray(ly) ? ly : []).filter((l) => num(l.qty_remaining) > 0))
        setMoves(Array.isArray(mv) ? mv : [])
        setBins(bn)
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load this product.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [productId, binTracking])

  const st = item && stockState(item)
  const onHand = num(item?.qty_on_hand)
  const price = num(item?.unit_price)
  const cost = unitCost(item)
  const margin = price > 0 ? ((price - cost) / price) * 100 : null
  const levelTotal = levels.reduce((s, l) => s + num(l.quantity), 0)
  const layerQty = layers.reduce((s, l) => s + num(l.qty_remaining), 0)
  const layerValue = layers.reduce((s, l) => s + num(l.value ?? num(l.qty_remaining) * num(l.unit_cost)), 0)
  const off = (a, b) => Math.abs(a - b) > 0.0005

  return (
    <div className="ci-form-page">
      <DocHeader
        back={<BackLink to="/inventory/stock" label="Stock" />}
        title={item ? (item.name || item.sku) : 'Product'}
        badge={st && <StatusBadge label={st.label} tone={st.tone} />}
        meta={item && [item.sku && `SKU ${item.sku}`, item.barcode && `Barcode ${item.barcode}`].filter(Boolean).join(' · ')}
        actions={item && (
          <>
            {levels.length > 1 && <Link className="ci-btn ci-btn-ghost" to={`/inventory/move?product_id=${item.product_id}`}><ArrowLeftRight size={14} />Move</Link>}
            {canBuy && <Link className="ci-btn ci-btn-ghost" to="/purchase/po-form/new"><ShoppingCart size={14} />Order more</Link>}
            <Link className="ci-btn ci-btn-primary" to={`/inventory/adjust?product_id=${item.product_id}`}><SlidersHorizontal size={14} />Adjust stock</Link>
          </>
        )}
      />

      {error && <ErrorBanner message={error} />}

      {loading ? <Spinner label="Loading the product…" /> : item && (
        <>
          <section className="doc-card">
            <dl className="inv-facts">
              <div><dt>On hand</dt><dd>{qty(onHand)}</dd></div>
              <div><dt>Cost each</dt><dd>{money(cost)}</dd></div>
              <div><dt>Stock value</dt><dd>{money(stockValue(item))}</dd></div>
              <div><dt>Selling price</dt><dd>{price > 0 ? money(price) : '–'}</dd></div>
              <div><dt>Margin</dt><dd>{margin == null ? '–' : `${margin.toFixed(1)}%`}</dd></div>
            </dl>
          </section>

          {levels.length > 0 && (
            <section className="doc-card">
              <div className="doc-card-head">
                <h2>By location</h2>
                {off(levelTotal, onHand) && <span className="sum-warn">These add up to {qty(levelTotal)}, but {qty(onHand)} are on hand</span>}
              </div>
              <div className="doc-table-scroll">
                <table className="ci-line-table">
                  <thead><tr><th>Location</th><th className="num">On hand</th></tr></thead>
                  <tbody>
                    {levels.map((l) => (
                      <tr key={l.location_id}><td>{l.location_name}</td><td className="num">{qty(l.quantity)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {binTracking && bins && (
            <section className="doc-card">
              <div className="doc-card-head">
                <h2>Bins</h2>
                <span>{bins.pick_path ? `Picked from ${bins.pick_path}` : 'No pick bin set'}</span>
              </div>
              {(bins.rows || []).length ? (
                <div className="doc-table-scroll">
                  <table className="ci-line-table">
                    <thead><tr><th>Location</th><th>Bin</th><th className="num">On hand</th></tr></thead>
                    <tbody>
                      {bins.rows.map((b, i) => <tr key={`${b.location}-${b.path}-${i}`}><td>{b.location}</td><td>{b.path}</td><td className="num">{qty(b.qty)}</td></tr>)}
                    </tbody>
                  </table>
                </div>
              ) : <p className="doc-empty">Not in any bin yet.</p>}
            </section>
          )}

          <section className="doc-card">
            <div className="doc-card-head">
              <h2>Cost batches</h2>
              <span>{off(layerQty, onHand) ? <span className="sum-warn">These add up to {qty(layerQty)} ({money(layerValue)}), but {qty(onHand)} are on hand</span> : 'Oldest are sold first'}</span>
            </div>
            {layers.length ? (
              <div className="doc-table-scroll">
                <table className="ci-line-table">
                  <thead><tr><th>Added</th><th>From</th><th className="num">Left</th><th className="num">Cost each</th><th className="num">Value</th></tr></thead>
                  <tbody>
                    {layers.map((l) => (
                      <tr key={l.layer_id}>
                        <td>{fmtDay(l.created_at) || '–'}</td>
                        <td>{l.reason || '–'}{l.location_name && <span className="cell-sub">{l.location_name}</span>}</td>
                        <td className="num">{qty(l.qty_remaining)}</td>
                        <td className="num">{money(l.unit_cost)}</td>
                        <td className="num">{money(l.value ?? num(l.qty_remaining) * num(l.unit_cost))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="doc-empty">No cost batches. They’re added when goods are received.</p>}
          </section>

          <section className="doc-card">
            <div className="doc-card-head"><h2>Recent moves</h2><span><Link to="/inventory/movements">All moves</Link></span></div>
            {moves.length ? (
              <div className="doc-table-scroll">
                <table className="ci-line-table">
                  <thead><tr><th>When</th><th>What</th><th>From</th><th>To</th><th className="num">Qty</th></tr></thead>
                  <tbody>
                    {moves.map((m, i) => (
                      <tr key={m.movement_id ?? i}>
                        <td>{fmtDay(m.movement_date) || '–'}</td>
                        <td>{MOVE_TYPES[m.move_type] || m.move_type}{m.notes && <span className="cell-sub">{m.notes}</span>}</td>
                        <td>{[m.from_location, m.from_area].filter(Boolean).join(' · ') || '–'}</td>
                        <td>{[m.to_location, m.to_area].filter(Boolean).join(' · ') || '–'}</td>
                        <td className="num">{qty(m.quantity)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="doc-empty">This product hasn’t been moved between locations.</p>}
          </section>
        </>
      )}
    </div>
  )
}
